import { useCallback, useEffect, useRef, useState } from "react";
import { uniq } from "lodash";
import {
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
  GET_CIDM_GROUPS,
} from "../../../gql/bookingapi/queries";
import { convertNumericValueToBoolean } from "../../../utils/helpers/data";

const toUserId = (value) => (value ? String(value).trim() : null);
const toEmailKey = (value) => (value ? String(value).trim().toLowerCase() : null);
const EMPTY_CANDIDATES = [];

const getEquipmentAccessData = (item = {}) => {
  const addInfo = item?.addInfo || {};
  const inventoryId = item?.inventoryId || addInfo?.inventoryId || item?.id || null;
  const isBookingControlledRaw =
    addInfo?.isBookingControlled
    ?? item?.isBookingControlled
    ?? item?.is_booking_controlled;
  const bookableForUsers =
    addInfo?.bookableForUsers
    ?? item?.bookableForUsers
    ?? item?.bookable_for_users
    ?? [];
  const cidmGroupName =
    addInfo?.cidmGroupName
    ?? addInfo?.cidm_group_name
    ?? item?.cidmGroupName
    ?? item?.cidm_group_name
    ?? null;

  return {
    inventoryId,
    isBookingControlled: convertNumericValueToBoolean(isBookingControlledRaw),
    bookableForUsers,
    cidmGroupName,
  };
};

/**
 * Resolves eligible "Booking For" users by intersecting the allow-lists for
 * selected controlled equipment. Emails in access configuration are resolved
 * to user IDs from the selector options, then checked by user ID only, matching
 * the backend's authorization comparison.
 */
export const useEligibleBookingForUsers = (instrumentDetails, client, candidates = EMPTY_CANDIDATES) => {
  const accessConfigCacheRef = useRef(new Map());
  const cidmGroupCacheRef = useRef(new Map());
  const [eligibleUserIds, setEligibleUserIds] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const candidateIdsByEmail = new Map();
    (candidates || []).forEach((candidate) => {
      const userId = toUserId(candidate?.userId);
      const email = toEmailKey(candidate?.email);
      if (userId && email && !candidateIdsByEmail.has(email)) {
        candidateIdsByEmail.set(email, userId);
      }
    });
    const resolveAccessConfig = async (inventoryId, bookableForUsers, fallbackCidmGroupName) => {
      if (!inventoryId) {
        return {
          bookableForUsers: bookableForUsers || [],
          cidmGroupName: fallbackCidmGroupName || null,
        };
      }

      if (accessConfigCacheRef.current.has(inventoryId)) {
        return accessConfigCacheRef.current.get(inventoryId);
      }

      let resolved = {
        bookableForUsers: bookableForUsers || [],
        cidmGroupName: fallbackCidmGroupName || null,
      };
      try {
        const { data } = await client.query({
          query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
          variables: {
            limit: 20,
            modelName: "BOOKINGACCESSCONFIG",
            inventoryId: { eq: inventoryId },
          },
          fetchPolicy: "no-cache",
        });
        const configItems = data?.listBookingAccessConfigurationByEquipment?.items || [];
        const groups = configItems.flatMap((config) => config?.groups?.items || []);
        const directUserEmails = groups.flatMap((group) => (group?.users || []).map((user) => user.email));
        const cidmGroupName =
          groups.find((group) => group?.cidmGroup?.groupName)?.cidmGroup?.groupName
          || fallbackCidmGroupName
          || null;
        resolved = {
          bookableForUsers: uniq([...(bookableForUsers || []), ...directUserEmails]),
          cidmGroupName,
        };
      } catch (err) {
        console.error("Error loading booking access config for", inventoryId, err);
      }

      accessConfigCacheRef.current.set(inventoryId, resolved);
      return resolved;
    };

    const resolveCidmGroupIds = (cidmGroupName) => {
      if (!cidmGroupName) return null;
      if (cidmGroupCacheRef.current.has(cidmGroupName)) {
        return cidmGroupCacheRef.current.get(cidmGroupName);
      }

      const request = (async () => {
        try {
          const { data } = await client.query({
            query: GET_CIDM_GROUPS,
            variables: { input: { groupName: cidmGroupName } },
            fetchPolicy: "cache-first",
          });
          const members = data?.getCidmGroups || [];
          return new Set(members.map((member) => toUserId(member?.userId)).filter(Boolean));
        } catch (err) {
          console.error("Error loading CIDM group members for", cidmGroupName, err);
          return null;
        }
      })();
      cidmGroupCacheRef.current.set(cidmGroupName, request);
      return request;
    };

    const resolveAllowedUserId = (value) => {
      const accessValue = toUserId(value);
      if (!accessValue) return null;
      return candidateIdsByEmail.get(toEmailKey(accessValue)) || accessValue;
    };

    const resolve = async () => {
      const controlledEquipment = (instrumentDetails || [])
        .map(getEquipmentAccessData)
        .filter((item) => item?.isBookingControlled && item?.inventoryId);

      if (controlledEquipment.length === 0) {
        if (!cancelled) setEligibleUserIds(null);
        return;
      }

      const allowLists = await Promise.all(
        controlledEquipment.map(async (item) => {
          const { bookableForUsers, cidmGroupName } = await resolveAccessConfig(
            item.inventoryId,
            item.bookableForUsers,
            item.cidmGroupName,
          );
          const cidmGroupIds = await resolveCidmGroupIds(cidmGroupName);
          const directIds = new Set((bookableForUsers || []).map(resolveAllowedUserId).filter(Boolean));
          const hasCidmGroupIds = !!cidmGroupIds && cidmGroupIds.size > 0;

          if (directIds.size === 0 && !hasCidmGroupIds) return null;
          return hasCidmGroupIds ? new Set([...directIds, ...cidmGroupIds]) : directIds;
        }),
      );

      if (cancelled) return;

      const usableAllowLists = allowLists.filter(Boolean);
      if (usableAllowLists.length === 0) {
        setEligibleUserIds(null);
        return;
      }

      const [first, ...rest] = usableAllowLists;
      const intersection = new Set(
        [...first].filter((userId) => rest.every((set) => set.has(userId))),
      );
      setEligibleUserIds(intersection);
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [instrumentDetails, client, candidates]);

  const isUserEligible = useCallback(
    (candidate) =>
      eligibleUserIds === null
        ? true
        : eligibleUserIds.has(toUserId(candidate?.userId)),
    [eligibleUserIds],
  );

  return { isUserEligible, isFiltering: eligibleUserIds !== null, eligibleUserIds };
};
